import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';

const EditConstituencyModal = ({ constituency, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: constituency.code || '',
    name: constituency.name || '',
    district: constituency.district || '',
    phase: constituency.phase || '',  // Add Phase
    pollDate: constituency.pollDate ? constituency.pollDate.split('T')[0] : '',
    resultDate: constituency.resultDate ? constituency.resultDate.split('T')[0] : ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      console.log('📤 Updating constituency:', constituency.id);

      // Prepare update data
      const updateData = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        district: formData.district.trim(),
        stateId: constituency.stateId || 1,
        phase: formData.phase ? parseInt(formData.phase) : null,  // Parse Phase
        pollDate: formData.pollDate || null,
        resultDate: formData.resultDate || null
      };

      await api.updateConstituency(constituency.id, updateData);
      
      onSuccess();
    } catch (error) {
      console.error('❌ Update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update constituency'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <i className="fas fa-edit"></i>
            Edit Constituency
          </h3>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        {message && (
          <div className={`alert ${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-body">
          {/* Constituency Details */}
          <div className="form-section">
            <h4><i className="fas fa-map-marked-alt"></i> Constituency Details</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="code">
                  Constituency Code *
                </label>
                <input
                  id="code"
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="AC001"
                  required
                  maxLength={10}
                />
                <small className="field-hint">
                  Format: AC001, AC002, etc.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="name">
                  Constituency Name *
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter constituency name"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="district">
                  District *
                </label>
                <input
                  id="district"
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  placeholder="Enter district name"
                  required
                />
              </div>

              {/* Phase Dropdown */}
              <div className="form-group">
                <label htmlFor="phase">
                  <i className="fas fa-layer-group"></i>
                  Election Phase *
                </label>
                <select
                  id="phase"
                  name="phase"
                  value={formData.phase}
                  onChange={handleChange}
                  required
                  className="phase-select"
                >
                  <option value="">Select Phase...</option>
                  <option value="1">Phase 1 - 6 November 2025</option>
                  <option value="2">Phase 2 - 11 November 2025</option>
                </select>
                <small className="field-hint">
                  Select the voting phase for this constituency
                </small>
              </div>
            </div>
          </div>

          {/* Election Dates */}
          <div className="form-section">
            <h4><i className="fas fa-calendar-alt"></i> Election Dates</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pollDate">
                  Poll Date *
                </label>
                <input
                  id="pollDate"
                  type="date"
                  name="pollDate"
                  value={formData.pollDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="resultDate">
                  Result Date *
                </label>
                <input
                  id="resultDate"
                  type="date"
                  name="resultDate"
                  value={formData.resultDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>
        </form>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={loading}>
            Cancel
          </button>
          <button type="submit" onClick={handleSubmit} className="submit-btn" disabled={loading}>
            {loading ? (
              <>
                <div className="spinner-small"></div>
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Update Constituency
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditConstituencyModal;
